/** @format */
import isPropValid from '@emotion/is-prop-valid';
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import { styles } from './tab.styles';

export enum TabVariant {
    NAVIGATION = 'NAVIGATION',
    INFO = 'INFO',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface TabProps {
    /**
     * The tab ID
     */
    id?: string;

    /**
     * Whether the tab is active
     *
     * @default false
     */
    active?: boolean;

    /**
     * Whether the tab is disabled
     *
     * @default false
     */
    disabled?: boolean;

    /**
     * Link URL
     */
    href?: string;

    /**
     * Override the natural tab index of the element
     */
    tabIndex?: number;

    /**
     * Child elements
     */
    children?: ReactNode;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Mouse click event handler.
     *
     * @param event {MouseEvent<HTMLElement>} The mouse event
     */
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;

    /**
     * Element blur event handler.
     *
     * @param event {FocusEvent<HTMLElement>} The blur event
     */
    onBlur?: (event: React.FocusEvent<HTMLElement>) => void;

    /**
     * Element focus event handler.
     *
     * @param event {FocusEvent<HTMLElement>} The focus event
     */
    onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
    variant?: TabVariant;
}

/**
 * Get the base element for our tab, either a button or an anchor.
 *
 * @param props {TabProps} the tab properties
 */
const BaseElement = ({
    active, // destructure to remove from props passed to HTML element
    href,
    children,
    ...otherProps
}: TabProps & { isOpen?: boolean; role: 'tab' }) => {
    return React.createElement(
        href ? 'a' : 'button',
        { href, ...otherProps },
        children
    );
};

const StyledTab = styled(BaseElement).withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const Tab: FunctionComponent<TabProps> = ({
    active = false,
    disabled = false,
    href,
    children,
    variant = TabVariant.NAVIGATION,
    ...otherProps
}: TabProps) => {
    return (
        <StyledTab
            active={active}
            disabled={disabled}
            href={disabled ? undefined : href}
            role="tab"
            variant={variant}
            {...otherProps}
        >
            {children}
        </StyledTab>
    );
};
